import { useEffect, useState } from "react";
import { DayPicker } from "react-day-picker";
import { ptBR } from "date-fns/locale";
import { format, differenceInCalendarDays, startOfToday } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Heart, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { DECORACOES, CONTATO } from "../data/chales";
import "react-day-picker/dist/style.css";

const API = process.env.REACT_APP_BACKEND_URL;
const brl = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const maskCPF = (v) =>
  v
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

const maskTel = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length > 10) return d.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3");
  if (d.length > 6) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
  if (d.length > 2) return d.replace(/(\d{2})(\d{0,5})/, "($1) $2");
  return d;
};

const INICIAL = {
  nome: "",
  nascimento: "",
  cpf: "",
  email: "",
  telefone: "",
  acompanhante: "",
  hospedes: 2,
  pet: "",
  pagamento: "",
  parcelas: 1,
  solicitacao: "",
};

const inputCls =
  "w-full rounded-none border border-[#1C3026]/20 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#8C5A40]";

const Campo = ({ label, children, full }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-[#1C3026]">
      {label}
    </span>
    {children}
  </label>
);

export default function BookingDialog({ aberto, onFechar, nome, categoria }) {
  const [range, setRange] = useState();
  const [querDecoracao, setQuerDecoracao] = useState(null);
  const [decoracaoId, setDecoracaoId] = useState(null);
  const [form, setForm] = useState(INICIAL);
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [meses, setMeses] = useState(2);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const aplicar = () => setMeses(mq.matches ? 1 : 2);
    aplicar();
    mq.addEventListener("change", aplicar);
    return () => mq.removeEventListener("change", aplicar);
  }, []);

  const noites = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0;
  const decoracao = DECORACOES.find((d) => d.id === decoracaoId) || null;
  const petTaxa = form.pet === "Sim" ? 200 : 0;
  const total = noites * categoria.diaria + (querDecoracao && decoracao ? decoracao.preco : 0) + petTaxa;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fechar = () => {
    onFechar();
    setTimeout(() => {
      setRange(undefined);
      setQuerDecoracao(null);
      setDecoracaoId(null);
      setForm(INICIAL);
      setSucesso(false);
    }, 300);
  };

  const enviar = async () => {
    if (!range?.from || !range?.to || noites < 1)
      return toast.error("Escolha as datas de check-in e check-out no calendário.");
    if (querDecoracao === null)
      return toast.error("Informe se deseja decoração romântica (Sim ou Não).");
    if (querDecoracao && !decoracao)
      return toast.error("Escolha uma das opções de decoração romântica.");
    if (!form.nome.trim()) return toast.error("Informe seu nome completo.");
    if (!form.nascimento) return toast.error("Informe sua data de nascimento.");
    if (form.cpf.replace(/\D/g, "").length !== 11) return toast.error("Informe um CPF válido.");
    if (!/.+@.+\..+/.test(form.email)) return toast.error("Informe um e-mail válido.");
    if (form.telefone.replace(/\D/g, "").length < 10)
      return toast.error("Informe seu telefone (WhatsApp).");
    if (!form.acompanhante.trim()) return toast.error("Informe o nome do acompanhante.");
    if (!form.pet) return toast.error("Informe se levará pet (Sim ou Não).");
    if (!form.pagamento) return toast.error("Escolha a forma de pagamento.");

    setEnviando(true);
    try {
      await axios.post(`${API}/api/reservas`, {
        chale: `Chalé ${nome}`,
        categoria: categoria.titulo,
        checkin: format(range.from, "yyyy-MM-dd"),
        checkout: format(range.to, "yyyy-MM-dd"),
        noites,
        diaria: categoria.diaria,
        decoracao: querDecoracao && decoracao ? { nome: decoracao.nome, preco: decoracao.preco } : null,
        total,
        nome_completo: form.nome.trim(),
        data_nascimento: form.nascimento,
        cpf: form.cpf,
        email: form.email.trim(),
        telefone: form.telefone,
        acompanhante: form.acompanhante.trim(),
        hospedes: Number(form.hospedes),
        pet: form.pet,
        pet_taxa: petTaxa,
        pagamento: {
          tipo: form.pagamento,
          parcelas: form.pagamento === "cartao" ? Number(form.parcelas) : null,
        },
        solicitacao_especial: form.solicitacao.trim(),
      });
      setSucesso(true);
    } catch (e) {
      toast.error("Não foi possível enviar sua reserva. Tente novamente ou fale conosco pelo WhatsApp.");
    } finally {
      setEnviando(false);
    }
  };

  const opcaoBtn = (ativo) =>
    `flex-1 border-2 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
      ativo
        ? "border-[#8C5A40] bg-[#8C5A40]/10 text-[#1C3026]"
        : "border-[#1C3026]/15 text-[#5B7065] hover:border-[#1C3026]/40"
    }`;

  return (
    <Dialog open={aberto} onOpenChange={(o) => !o && fechar()}>
      <DialogContent
        className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl gap-0 overflow-y-auto rounded-none border-[#1C3026]/20 p-0"
        data-testid={`reserva-dialog-${nome.toLowerCase()}`}
      >
        {sucesso ? (
          <div className="flex flex-col items-center px-6 py-16 text-center">
            <CheckCircle2 className="h-16 w-16 text-[#3D6B51]" />
            <h3 className="mt-6 text-2xl font-bold md:text-3xl" data-testid="reserva-sucesso-titulo">
              Sua reserva foi concluída com Sucesso!
            </h3>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-[#5B7065] md:text-base">
              Recebemos todos os seus dados por WhatsApp e e-mail. Nossa equipe entrará em contato
              para confirmar a sua estadia no Chalé {nome}.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a
                href={`https://wa.me/${CONTATO.whatsapp}?text=${encodeURIComponent(
                  `Olá! Acabei de enviar uma solicitação de reserva para o Chalé ${nome}.`
                )}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 bg-[#1C3026] px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-[#F6F5F2] transition-colors hover:bg-[#8C5A40]"
                data-testid="reserva-sucesso-whatsapp"
              >
                <MessageCircle className="h-4 w-4" /> Falar no WhatsApp
              </a>
              <button
                onClick={fechar}
                className="border border-[#1C3026]/30 px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors hover:bg-[#EBE8E0]"
                data-testid="reserva-sucesso-fechar"
              >
                Fechar
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-[#1C3026] px-6 py-6 text-[#F6F5F2] md:px-8">
              <DialogHeader>
                <p className="text-left text-[10px] font-bold uppercase tracking-[0.3em] text-[#d8c9b0]">
                  {categoria.titulo}
                </p>
                <DialogTitle className="mt-1 text-left text-2xl font-bold md:text-3xl">
                  Chalé {nome}
                </DialogTitle>
              </DialogHeader>
              <p className="mt-2 text-sm text-[#F6F5F2]/80">
                {brl(categoria.diaria)} por noite — café da manhã incluso
              </p>
            </div>

            <div className="space-y-10 px-6 py-8 md:px-8">
              <section>
                <h4 className="text-xs font-bold uppercase tracking-[0.25em] text-[#8C5A40]">
                  1. Escolha as datas
                </h4>
                <p className="mt-1 text-sm text-[#5B7065]">
                  Todas as datas estão disponíveis — selecione o check-in e o check-out.
                </p>
                <div
                  className="mt-4 flex justify-center border border-[#1C3026]/15 bg-white p-4"
                  data-testid="reserva-calendario"
                >
                  <DayPicker
                    mode="range"
                    selected={range}
                    onSelect={setRange}
                    numberOfMonths={meses}
                    locale={ptBR}
                    fromDate={startOfToday()}
                    disabled={{ before: startOfToday() }}
                  />
                </div>
                {noites > 0 && (
                  <p className="mt-3 text-sm font-bold" data-testid="reserva-noites">
                    {noites} {noites === 1 ? "diária" : "diárias"} —{" "}
                    {format(range.from, "dd/MM/yyyy")} até {format(range.to, "dd/MM/yyyy")}
                  </p>
                )}
              </section>

              <section>
                <h4 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#8C5A40]">
                  <Heart className="h-4 w-4" /> 2. Decoração romântica?
                </h4>
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={() => {
                      setQuerDecoracao(false);
                      setDecoracaoId(null);
                    }}
                    className={opcaoBtn(querDecoracao === false)}
                    data-testid="decoracao-nao"
                  >
                    Não
                  </button>
                  <button
                    onClick={() => setQuerDecoracao(true)}
                    className={opcaoBtn(querDecoracao === true)}
                    data-testid="decoracao-sim"
                  >
                    Sim
                  </button>
                </div>
                {querDecoracao && (
                  <div className="mt-4 space-y-4">
                    <div className="grid gap-3 md:grid-cols-3">
                      {DECORACOES.map((d) => (
                        <button
                          key={d.id}
                          onClick={() => setDecoracaoId(d.id)}
                          className={`border-2 p-4 text-left transition-colors ${
                            decoracaoId === d.id
                              ? "border-[#8C5A40] bg-[#8C5A40]/10"
                              : "border-[#1C3026]/15 hover:border-[#1C3026]/40"
                          }`}
                          data-testid={`decoracao-opcao-${d.id}`}
                        >
                          <p className="text-xs font-bold uppercase tracking-wider">{d.nome}</p>
                          <p className="mt-1 text-lg font-bold text-[#8C5A40]">{brl(d.preco)}</p>
                          <p className="mt-2 text-[11px] leading-relaxed text-[#5B7065]">{d.desc}</p>
                        </button>
                      ))}
                    </div>
                    <img
                      src="/decoracao-romantica.jpeg"
                      alt="Referência das opções de decoração romântica"
                      className="max-h-80 w-full border border-[#1C3026]/15 bg-white object-contain"
                      data-testid="decoracao-referencia"
                    />
                  </div>
                )}
              </section>

              <section className="border border-[#1C3026]/15 bg-[#EBE8E0] p-5" data-testid="reserva-resumo">
                <h4 className="text-xs font-bold uppercase tracking-[0.25em] text-[#1C3026]">
                  Resumo do valor
                </h4>
                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#5B7065]">Diárias</span>
                    <span data-testid="reserva-diarias-valor">
                      {noites > 0 ? `${noites} × ${brl(categoria.diaria)}` : "Selecione as datas"}
                    </span>
                  </div>
                  {querDecoracao && decoracao && (
                    <div className="flex justify-between">
                      <span className="text-[#5B7065]">{decoracao.nome}</span>
                      <span>{brl(decoracao.preco)}</span>
                    </div>
                  )}
                  {petTaxa > 0 && (
                    <div className="flex justify-between">
                      <span className="text-[#5B7065]">Taxa pet</span>
                      <span data-testid="reserva-pet-taxa">{brl(petTaxa)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-[#1C3026]/20 pt-2 text-lg font-bold">
                    <span>Total</span>
                    <span data-testid="reserva-total">{brl(total)}</span>
                  </div>
                </div>
                {form.pagamento === "cartao" && total > 0 && (
                  <p className="mt-2 text-xs text-[#5B7065]" data-testid="reserva-parcelamento">
                    ou {form.parcelas}x de {brl(total / Number(form.parcelas))} sem juros no cartão
                  </p>
                )}
              </section>

              <section>
                <h4 className="text-xs font-bold uppercase tracking-[0.25em] text-[#8C5A40]">
                  3. Seus dados
                </h4>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <Campo label="Nome completo" full>
                    <input
                      className={inputCls}
                      value={form.nome}
                      onChange={(e) => set("nome", e.target.value)}
                      placeholder="Seu nome completo"
                      data-testid="reserva-nome"
                    />
                  </Campo>
                  <Campo label="Data de nascimento">
                    <input
                      type="date"
                      className={inputCls}
                      value={form.nascimento}
                      max={format(new Date(), "yyyy-MM-dd")}
                      onChange={(e) => set("nascimento", e.target.value)}
                      data-testid="reserva-nascimento"
                    />
                  </Campo>
                  <Campo label="CPF">
                    <input
                      className={inputCls}
                      inputMode="numeric"
                      value={form.cpf}
                      onChange={(e) => set("cpf", maskCPF(e.target.value))}
                      placeholder="000.000.000-00"
                      data-testid="reserva-cpf"
                    />
                  </Campo>
                  <Campo label="E-mail">
                    <input
                      type="email"
                      className={inputCls}
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder="voce@email.com"
                      data-testid="reserva-email"
                    />
                  </Campo>
                  <Campo label="Telefone (WhatsApp)">
                    <input
                      className={inputCls}
                      inputMode="tel"
                      value={form.telefone}
                      onChange={(e) => set("telefone", maskTel(e.target.value))}
                      placeholder="(00) 00000-0000"
                      data-testid="reserva-telefone"
                    />
                  </Campo>
                  <Campo label="Nome do acompanhante">
                    <input
                      className={inputCls}
                      value={form.acompanhante}
                      onChange={(e) => set("acompanhante", e.target.value)}
                      placeholder="Nome completo do acompanhante"
                      data-testid="reserva-acompanhante"
                    />
                  </Campo>
                  <Campo label="Número de hóspedes">
                    <select
                      className={inputCls}
                      value={form.hospedes}
                      onChange={(e) => set("hospedes", e.target.value)}
                      data-testid="reserva-hospedes"
                    >
                      {Array.from({ length: 8 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>
                          {n} {n === 1 ? "hóspede" : "hóspedes"}
                        </option>
                      ))}
                    </select>
                  </Campo>
                  <Campo label="Leva pet? (taxa de R$ 200,00 se sim)">
                    <div className="flex gap-2" data-testid="reserva-pet">
                      {["Sim", "Não"].map((o) => (
                        <button
                          key={o}
                          type="button"
                          onClick={() => set("pet", o)}
                          className={opcaoBtn(form.pet === o)}
                          data-testid={`reserva-pet-${o.toLowerCase()}`}
                        >
                          {o}
                        </button>
                      ))}
                    </div>
                  </Campo>
                  <Campo label="Forma de pagamento" full>
                    <div className="flex flex-wrap gap-2" data-testid="reserva-pagamento">
                      <button
                        type="button"
                        onClick={() => set("pagamento", "pix")}
                        className={opcaoBtn(form.pagamento === "pix")}
                        data-testid="reserva-pagamento-pix"
                      >
                        Pix
                      </button>
                      <button
                        type="button"
                        onClick={() => set("pagamento", "cartao")}
                        className={opcaoBtn(form.pagamento === "cartao")}
                        data-testid="reserva-pagamento-cartao"
                      >
                        Cartão de crédito — até 6x sem juros
                      </button>
                    </div>
                  </Campo>
                  {form.pagamento === "cartao" && (
                    <Campo label="Parcelas (sem juros)" full>
                      <select
                        className={inputCls}
                        value={form.parcelas}
                        onChange={(e) => set("parcelas", e.target.value)}
                        data-testid="reserva-parcelas"
                      >
                        {[1, 2, 3, 4, 5, 6].map((p) => (
                          <option key={p} value={p}>
                            {p}x sem juros{total > 0 ? ` — ${brl(total / p)} por parcela` : ""}
                          </option>
                        ))}
                      </select>
                    </Campo>
                  )}
                  <Campo label="Alguma solicitação especial?" full>
                    <textarea
                      rows={3}
                      className={inputCls}
                      value={form.solicitacao}
                      onChange={(e) => set("solicitacao", e.target.value)}
                      placeholder="Conte-nos se há algo especial para a sua estadia (opcional)"
                      data-testid="reserva-solicitacao"
                    />
                  </Campo>
                </div>
              </section>

              <button
                onClick={enviar}
                disabled={enviando}
                className="w-full bg-[#1C3026] py-4 text-xs font-bold uppercase tracking-[0.25em] text-[#F6F5F2] transition-colors hover:bg-[#8C5A40] disabled:opacity-50"
                data-testid="reserva-enviar"
              >
                {enviando ? "Enviando..." : "Enviar solicitação de reserva"}
              </button>
              <p className="text-center text-xs text-[#5B7065]">
                Sem pagamento online — os dados são enviados diretamente para o nosso WhatsApp e
                e-mail.
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
